import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Alert, Button, PageContainer, Spinner } from "@swot/base-components";
import { CmtsEnterpriseForm, type CmtsFormField } from "../forms/index.js";
import { CmtsApiErrorPanel } from "../CmtsApiErrorPanel.js";
import { CmtsValidationErrorPanel } from "../CmtsValidationErrorPanel.js";
import { complaintApi } from "../../services/complaintApi.js";
import { complaintDropdownApi } from "../../services/complaintDropdownApi.js";
import { createIdempotencyKey, normalizeApiError } from "../../services/api.service.js";
import { normalizeComplaintUiError } from "../../core/errors/cmtsComplaintErrorNormalizer.js";
import { readCmtsSession } from "../../core/auth/authStore.js";
import { buildComplaintCreateRequest, type ComplaintCreateContext, type ComplaintCreatePayload } from "../../core/payloads/cmtsTypedPayloads.js";
import type { CmtsComplaintDropdowns } from "../../core/complaints/cmtsComplaintLifecycleTypes.js";
import { normalizeUserSafeApiError } from "@cmts/api/apiErrorNormalizer.js";

const EMPTY_DROPDOWNS: CmtsComplaintDropdowns = { sourceId: [], complaintTypeId: [], categoryId: [], subCategoryId: [], severityId: [], priorityId: [], organizationUnitId: [], typeId: [], subcategoryId: [] };

function buildFields(params: {
  dropdowns: CmtsComplaintDropdowns;
  hasCategory: boolean;
  subcategoriesLoading: boolean;
  selectedSourceCode?: string;
}): readonly CmtsFormField[] {
  const { dropdowns, hasCategory, subcategoriesLoading } = params;
  const subcategoryOptions = dropdowns.subCategoryId ?? [];

  let subcategoryPlaceholder = "Select Subcategory";
  if (!hasCategory) {
    subcategoryPlaceholder = "Select Category first";
  } else if (subcategoriesLoading) {
    subcategoryPlaceholder = "Loading subcategories...";
  } else if (subcategoryOptions.length === 0) {
    subcategoryPlaceholder = "No subcategories for selected category";
  }

  const fields: CmtsFormField[] = [
    { name: "title", label: "Complaint title", required: true, maxLength: 250, placeholder: "Short issue title" },
    { name: "description", label: "Complaint description", type: "textarea", required: true, maxLength: 5000 },
    { name: "sourceId", label: "Source", type: "select", required: true, options: dropdowns.sourceId },
    { name: "complaintTypeId", label: "Type", type: "select", required: false, options: dropdowns.complaintTypeId },
    { name: "categoryId", label: "Category", type: "select", required: true, options: dropdowns.categoryId },
    {
      name: "subCategoryId",
      label: "Subcategory",
      type: "select",
      required: false,
      disabled: !hasCategory || subcategoryOptions.length === 0 || subcategoriesLoading,
      options: subcategoryOptions,
      placeholder: subcategoryPlaceholder,
    },
    { name: "severityId", label: "Severity", type: "select", required: true, options: dropdowns.severityId },
    { name: "priorityId", label: "Priority", type: "select", required: true, options: dropdowns.priorityId },
    { name: "organizationUnitId", label: "Organization unit", type: "select", required: false, options: dropdowns.organizationUnitId, help: "Used only when your session has more than one permitted organization unit." },
    { name: "reportedAt", label: "Reported date", type: "date" },
    { name: "customerReference", label: "Customer / supplier reference" },
    { name: "partNumber", label: "Part Number", placeholder: "e.g. PRT-10294" },
    { name: "batchNumber", label: "Batch / Lot Number", placeholder: "e.g. BATCH-2026-A" },
  ];

  return fields;
}

function readContextString(record: Record<string, unknown> | null | undefined, keys: readonly string[]): string | undefined {
  if (!record) return undefined;
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number" && Number.isFinite(value)) return String(value);
  }
  return undefined;
}

function readContextArray(record: Record<string, unknown> | null | undefined, keys: readonly string[]): readonly string[] | undefined {
  if (!record) return undefined;
  const ids: string[] = [];
  for (const key of keys) {
    const value = record[key];
    if (!Array.isArray(value)) continue;
    for (const item of value) {
      if (typeof item === "string" || typeof item === "number") {
        const id = String(item).trim();
        if (id) ids.push(id);
      } else if (typeof item === "object" && item !== null) {
        const id = readContextString(item as Record<string, unknown>, ["organizationUnitId", "OrganizationUnitId", "orgUnitId", "OrgUnitId", "id", "Id"]);
        if (id) ids.push(id);
      }
    }
  }
  return ids.length ? Array.from(new Set(ids)) : undefined;
}

function fieldFromMapperMessage(message: string): string {
  const match = /^(organizationUnitId|title|description|sourceId|customerId|supplierId|categoryId|severityId|priorityId|complaintTypeId|subCategoryId|partNumber|batchNumber|fieldValues)/.exec(message);
  return match?.[1] ?? "general";
}

function buildComplaintCreateContext(): ComplaintCreateContext {
  const session = readCmtsSession() as (Record<string, unknown> | null);
  return {
    organizationUnitId: readContextString(session, ["organizationUnitId", "OrganizationUnitId", "orgUnitId", "OrgUnitId"]),
    organizationUnitIds: readContextArray(session, ["organizationUnitIds", "OrganizationUnitIds", "orgScopes", "organizationScopes"]),
  };
}

function validatePayload(payload: Record<string, unknown>, context: ComplaintCreateContext): readonly { field: string; message: string }[] {
  const errors: { field: string; message: string }[] = [];
  for (const field of ["title", "description", "sourceId", "categoryId", "severityId", "priorityId"]) {
    if (!String(payload[field] ?? "").trim()) errors.push({ field, message: `${field} is required.` });
  }
  const scopedOrgUnits = context.organizationUnitIds?.filter(Boolean) ?? [];
  if (!String(payload.organizationUnitId ?? context.organizationUnitId ?? "").trim() && scopedOrgUnits.length !== 1) errors.push({ field: "organizationUnitId", message: "Organization unit is required from organization scope or selection." });
  if (String(payload.description ?? "").trim().length > 0 && String(payload.description ?? "").trim().length < 10) errors.push({ field: "description", message: "Complaint description must contain at least 10 characters." });
  return errors;
}

export function CmtsComplaintCreateForm() {
  const navigate = useNavigate();
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const categoryId = String(draft.categoryId ?? "").trim();

  // Reset subCategoryId whenever category changes to prevent submitting stale/mismatched data
  const prevCategoryIdRef = useRef(categoryId);
  useEffect(() => {
    if (prevCategoryIdRef.current !== categoryId) {
      prevCategoryIdRef.current = categoryId;
      setDraft((prev) => {
        if (!prev.subCategoryId) return prev;
        const { subCategoryId, ...rest } = prev;
        return rest;
      });
    }
  }, [categoryId]);

  const [localErrors, setLocalErrors] = useState<readonly { field: string; message: string }[]>([]);
  const [submitError, setSubmitError] = useState<unknown>(null);

  // 1. Fetch base dropdowns for the entire form
  const baseDropdownsQuery = useQuery({
    queryKey: ["cmts", "complaint-create-base-dropdowns"],
    queryFn: ({ signal }) => complaintDropdownApi.allForCreate(undefined, signal),
    staleTime: 60_000,
    retry: 1,
  });

  // 2. Fetch specific subcategories from backend whenever categoryId changes
  const subcategoriesQuery = useQuery({
    queryKey: ["cmts", "complaint-subcategories-cascading", categoryId],
    queryFn: ({ signal }) => complaintDropdownApi.subcategories(categoryId, { activeOnly: true }, signal),
    enabled: Boolean(categoryId),
    staleTime: 30_000,
    retry: 1,
  });

  const baseDropdowns = baseDropdownsQuery.data ?? EMPTY_DROPDOWNS;

  // 3. Compute matching subcategories for the selected category
  const filteredSubcategories = useMemo(() => {
    if (!categoryId) return [];

    // If dedicated subcategories query returned data, use it
    if (subcategoriesQuery.data && subcategoriesQuery.data.length > 0) {
      return subcategoriesQuery.data;
    }

    // Otherwise, match against all loaded subcategories in base dropdowns
    const selectedCategory = baseDropdowns.categoryId.find(
      (c) => c.id === categoryId || c.value === categoryId || c.code === categoryId
    );
    const targetId = String(selectedCategory?.id ?? selectedCategory?.value ?? categoryId).trim().toLowerCase();
    const targetCode = String(selectedCategory?.code ?? selectedCategory?.label ?? categoryId).trim().toLowerCase();

    return (baseDropdowns.subCategoryId ?? []).filter((sub) => {
      const parentCatId = String(sub.metadata?.parentCategoryId ?? sub.metadata?.parentId ?? "").trim().toLowerCase();
      const parentCatCode = String(sub.metadata?.parentCode ?? sub.metadata?.parentCategoryCode ?? sub.metadata?.parent ?? "").trim().toLowerCase();

      if (parentCatId && (parentCatId === targetId || parentCatId === targetCode)) return true;
      if (parentCatCode && (parentCatCode === targetCode || parentCatCode === targetId)) return true;
      return false;
    });
  }, [categoryId, subcategoriesQuery.data, baseDropdowns]);

  const resolvedDropdowns = useMemo<CmtsComplaintDropdowns>(() => {
    return {
      ...baseDropdowns,
      subCategoryId: filteredSubcategories,
      subcategoryId: filteredSubcategories,
    };
  }, [baseDropdowns, filteredSubcategories]);

  const schemaQuery = useQuery({ 
    queryKey: ["cmts", "complaint-create-schema"], 
    queryFn: ({ signal }) => complaintApi.createSchema(signal), 
    retry: 1 
  });

  const mutation = useMutation({
    mutationFn: async (payload: ComplaintCreatePayload) => {
      const local = validatePayload(payload, buildComplaintCreateContext());
      setLocalErrors(local);
      if (local.length > 0) throw new Error("Local complaint validation failed before backend submission.");
      const idempotencyKey = createIdempotencyKey("complaint-create");
      return complaintApi.create(payload, idempotencyKey);
    },
    onSuccess: (created) => {
      const id = created.complaintId;
      navigate(`/cmts/complaints/${encodeURIComponent(id)}`);
    },
    onError: (error) => setSubmitError(error),
  });

  const sourceId = String(draft.sourceId ?? "").trim();
  const selectedSourceOption = resolvedDropdowns.sourceId.find((s) => s.id === sourceId || s.value === sourceId || s.code === sourceId);
  const selectedSourceCode = selectedSourceOption?.code ?? selectedSourceOption?.label ?? "";
  
  const fields = useMemo(() => buildFields({
    dropdowns: resolvedDropdowns,
    hasCategory: Boolean(categoryId),
    subcategoriesLoading: subcategoriesQuery.isFetching,
    selectedSourceCode,
  }), [resolvedDropdowns, categoryId, subcategoriesQuery.isFetching, selectedSourceCode]);
  
  const normalizedSubmitError = submitError ? normalizeComplaintUiError(submitError, "Complaint create failed.") : null;
  const apiError = submitError ? normalizeApiError(submitError) : null;

 return (
  <PageContainer
    title="Create Complaint"
    subtitle="Create and submit a complaint using governed CMTS master data."
  >
    <div className="cmts-complaint-create">
      {/* Blue page introduction */}
      <header className="cmts-complaint-create__hero">
        <div className="cmts-complaint-create__hero-icon">
          <span aria-hidden="true">+</span>
        </div>

        <div className="cmts-complaint-create__hero-content">
          <span className="cmts-complaint-create__eyebrow">
            COMPLAINT MANAGEMENT
          </span>

          <h2>Create a new complaint</h2>

          <p>
            Enter the complaint details, classification,
            severity and ownership information. Fields marked
            as required must be completed before submission.
          </p>
        </div>

        <div className="cmts-complaint-create__hero-status">
          <span className="cmts-complaint-create__status-dot" />
          Backend validated
        </div>
      </header>

      {/* Progress / information strip */}
      <section className="cmts-complaint-create__steps">
        <div className="cmts-complaint-create__step">
          <span>1</span>

          <div>
            <strong>Complaint details</strong>
            <small>Title, description and source</small>
          </div>
        </div>

        <div className="cmts-complaint-create__step">
          <span>2</span>

          <div>
            <strong>Classification</strong>
            <small>Type, category and subcategory</small>
          </div>
        </div>

        <div className="cmts-complaint-create__step">
          <span>3</span>

          <div>
            <strong>Impact and ownership</strong>
            <small>Severity, priority and organization</small>
          </div>
        </div>
      </section>

      {baseDropdownsQuery.isLoading ||
      schemaQuery.isLoading ? (
        <div className="cmts-complaint-create__loading">
          <Spinner label="Loading complaint form and dropdown options" />
        </div>
      ) : null}

      {baseDropdownsQuery.error ? (
        <div className="cmts-complaint-create__message">
          <CmtsApiErrorPanel
            error={baseDropdownsQuery.error}
            title="Backend dropdown loading failed"
            onRetry={() =>
              void baseDropdownsQuery.refetch()
            }
          />
        </div>
      ) : null}

      {schemaQuery.error ? (
        <div className="cmts-complaint-create__message">
          <Alert
            title="Create schema unavailable"
            tone="warning"
          >
            Complaint creation will continue with the
            canonical fields because the backend schema
            endpoint did not return a usable schema.
          </Alert>
        </div>
      ) : null}

      {normalizedSubmitError ? (
        <div className="cmts-complaint-create__message">
          <CmtsApiErrorPanel
            error={submitError}
            title={
              normalizedSubmitError.userMessage
            }
          />
        </div>
      ) : null}

      <CmtsValidationErrorPanel
        fieldErrors={[
          ...localErrors,
          ...(apiError?.fieldErrors ?? []),
        ]}
      />

      {/* Main form card */}
      <section className="cmts-complaint-create__form-card">
        <div className="cmts-complaint-create__form-header">
          <div>
            <span className="cmts-complaint-create__section-number">
              01
            </span>

            <div>
              <h3>Complaint information</h3>

              <p>
                Provide accurate details to support routing,
                SLA calculation and workflow assignment.
              </p>
            </div>
          </div>

          <span className="cmts-complaint-create__required">
            * Required fields
          </span>
        </div>

        <div className="cmts-complaint-create__form-body">
          <CmtsEnterpriseForm
            fields={fields}
            initialValues={draft}
            submitLabel="Create complaint"
            busy={mutation.isPending}
            error={
              normalizedSubmitError?.userMessage ??
              null
            }
            onValuesChange={(values) =>
              setDraft(values)
            }
            onSubmit={async (payload) => {
              setSubmitError(null);
              setLocalErrors([]);
              setDraft(payload);

              try {
                const autoResolvedPayload: Record<string, unknown> = { ...payload };
                const srcCode = String(selectedSourceCode || "").toUpperCase();
                if (!autoResolvedPayload.customerId && (srcCode.includes("CUSTOMER") || !srcCode)) {
                  const defaultCustomer = resolvedDropdowns.customerId?.[0]?.id ?? resolvedDropdowns.customerId?.[0]?.value;
                  if (defaultCustomer) autoResolvedPayload.customerId = defaultCustomer;
                }
                if (!autoResolvedPayload.supplierId && srcCode.includes("SUPPLIER")) {
                  const defaultSupplier = resolvedDropdowns.supplierId?.[0]?.id ?? resolvedDropdowns.supplierId?.[0]?.value;
                  if (defaultSupplier) autoResolvedPayload.supplierId = defaultSupplier;
                }

                const subCat = String(autoResolvedPayload.subCategoryId ?? "").trim();
                const isGuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
                if (subCat && !isGuid.test(subCat)) {
                  const matched = resolvedDropdowns.subCategoryId?.find((s) => s.id === subCat || s.value === subCat || s.code === subCat);
                  if (matched && isGuid.test(String(matched.id || matched.value))) {
                    autoResolvedPayload.subCategoryId = matched.id || matched.value;
                  } else {
                    delete autoResolvedPayload.subCategoryId;
                  }
                }

                const typedPayload =
                  buildComplaintCreateRequest(
                    autoResolvedPayload,
                    buildComplaintCreateContext(),
                  );

                await mutation.mutateAsync(
                  typedPayload,
                );
              } catch (error: unknown) {
                const message =
                  normalizeUserSafeApiError(
                    error,
                  ).userMessage ||
                  "Complaint create validation failed before backend submission.";

                setLocalErrors([
                  {
                    field:
                      fieldFromMapperMessage(
                        message,
                      ),
                    message,
                  },
                ]);

                setSubmitError(error);
              }
            }}
          >
            <div className="cmts-complaint-create__save-note">
              <span
                className="cmts-complaint-create__save-icon"
                aria-hidden="true"
              >
                ✓
              </span>

              <div>
                <strong>
                  Secure backend submission
                </strong>

                <p>
                  The complaint is saved through the backend
                  using validation and an idempotency key.
                  This page navigates only after a complaint
                  ID is returned.
                </p>
              </div>
            </div>

            <div className="cmts-complaint-create__footer">
              <Button
                label="Cancel"
                variant="secondary"
                disabled={mutation.isPending}
                onClick={() =>
                  navigate(
                    "/cmts/complaints",
                  )
                }
              />
            </div>
          </CmtsEnterpriseForm>
        </div>
      </section>
    </div>
  </PageContainer>
);
}